import {put, call} from 'redux-saga/effects';
import EntityTimestamp from "../../entities/entityTimestamp";
import * as Actions from "../actions/entityTimestampActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type EntityTimestampAction = {
    entityTimestampId: string;
    entityTimestamp: EntityTimestamp;
    entityTimestamps: EntityTimestamp[];
};

export function* fetchOneEntityTimestamp(action: EntityTimestampAction) {
	try {
		const entityTimestamp: EntityTimestamp = yield call(api.findOne, 'entityTimestamp', action.entityTimestampId);

		// Now we can update the user interface
		yield put({type: Actions.ENTITYTIMESTAMP_FETCH_ONE_SUCCESS, entityTimestampId: action.entityTimestampId, entityTimestamp});
	}
	catch(err) {
		yield put({type: Actions.ENTITYTIMESTAMP_FETCH_ONE_FAILURE, entityTimestampId: action.entityTimestampId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {EntityTimestampAction} action
 */
export function* saveEntityTimestamp(action: EntityTimestampAction) {
    // console.debug('⮂ ENTITYTIMESTAMP_SAVE saga', action.entityTimestamp?.id);
    
	if (action.entityTimestamp === undefined || action.entityTimestamp === null) {
		console.error('saveEntityTimestamp received a null');
		throw new Error('saveEntityTimestamp received a null');
	}

	if (!(action.entityTimestamp instanceof EntityTimestamp)) {
		action.entityTimestamp = new EntityTimestamp(action.entityTimestamp);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'entityTimestamp', action.entityTimestamp);
		if (addResult.isErr()) {
			console.error(`⮂ saveEntityTimestamp: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ENTITYTIMESTAMP_SAVE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ENTITYTIMESTAMP_SAVE, entityTimestamp: action.entityTimestamp});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'entityTimestamp', action.entityTimestamp.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.ENTITYTIMESTAMP_SAVE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {EntityTimestampAction} action
 * @param {EntityTimestamp[]} action.entityTimestamps
 */
export function* saveManyEntityTimestamps(action: EntityTimestampAction) {
    // console.debug('⮂ ENTITYTIMESTAMP_SAVE_MANY saga', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));
    
    if (!Array.isArray(action.entityTimestamps)) {
        console.error('saveManyEntityTimestamps expected an array, received', action.entityTimestamps);
        throw new Error('saveManyEntityTimestamps expected an array');
    }

	try {
		const toSave: EntityTimestamp[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.entityTimestamps.length; i++) {
			const entityTimestamp = action.entityTimestamps[i];
			entityTimestamp.persisted = false;
			toSave.push(entityTimestamp);
		}

		const addResult = yield call(idb.add, 'entityTimestamp', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyEntityTimestamps: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ENTITYTIMESTAMP_SAVE_MANY_FAILURE, entityTimestamps: action.entityTimestamps, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ENTITYTIMESTAMP_SAVE_MANY, entityTimestamps: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.ENTITYTIMESTAMP_SAVE_MANY_FAILURE, entityTimestamps: action.entityTimestamps, status: 500, err});

		console.error(`Error saving ${action.entityTimestamps.length} entityTimestamps to database`, err);
	}
}

/**
 * @param {EntityTimestampAction} action
 */
export function* removeEntityTimestamp(action: EntityTimestampAction) {
    const userId = getCurrentUserId();
	if (!userId || action.entityTimestamp === undefined || action.entityTimestamp === null || !(action.entityTimestamp instanceof EntityTimestamp)) {
			console.error('removeEntityTimestamp expected type EntityTimestamp, received', action.entityTimestamp);
			throw new Error('removeEntityTimestamp expected type EntityTimestamp');
		}

	try {
		// Mark as deleted
		action.entityTimestamp.status = 'D';
		action.entityTimestamp.addedOn = newHRTimestamp();
		action.entityTimestamp.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'entityTimestamp', action.entityTimestamp);
		if (addResult.isErr()) {
			console.error(`⮂ removeEntityTimestamp: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ENTITYTIMESTAMP_REMOVE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ENTITYTIMESTAMP_REMOVE, entityTimestamp: action.entityTimestamp});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.ENTITYTIMESTAMP_REMOVE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this EntityTimestamp.
	*
	* @param {EntityTimestampAction} action
	*/
export function* restoreEntityTimestamp(action: EntityTimestampAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreEntityTimestamp expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.entityTimestamp === undefined || action.entityTimestamp === null) {
        console.error('restoreEntityTimestamp expected a EntityTimestamp, received null');
        throw new Error('restoreEntityTimestamp expected a EntityTimestamp, received null');
    }
    else if (!(action.entityTimestamp instanceof EntityTimestamp)) action.entityTimestamp = new EntityTimestamp(action.entityTimestamp); 
    
	try {
		// Mark as restored
		action.entityTimestamp.status = 'U';
		action.entityTimestamp.addedOn = newHRTimestamp();
		action.entityTimestamp.addedBy = userId;
		action.entityTimestamp.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'entityTimestamp', action.entityTimestamp);
		if (addResult.isErr()) {
			console.error(`⮂ restoreEntityTimestamp: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ENTITYTIMESTAMP_RESTORE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ENTITYTIMESTAMP_RESTORE, entityTimestamp: action.entityTimestamp});
	}
	catch(err) {
		yield put({type: Actions.ENTITYTIMESTAMP_RESTORE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, err});

		console.error('Error restoring from database', err);
	}
}